/**
 * Sudo Mode Modal
 * ===============
 * Modal for verifying password before sensitive operations.
 * Used for bank updates, team member additions, data exports, etc.
 */

import React, { useState, useEffect } from 'react';
import { useSecurity } from '../context/SecurityContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/form-input';
import { Shield, KeyRound, X, AlertTriangle } from 'lucide-react';

const SudoModeModal = () => {
  const { 
    showSudoModal, 
    handleSudoComplete, 
    handleSudoCancel, 
    verifySudo 
  } = useSecurity();
  
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (showSudoModal) {
      setPassword('');
      setError('');
      setLoading(false);
    }
  }, [showSudoModal]);

  if (!showSudoModal) {
    return null;
  }

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifySudo(password);
      if (result.success) {
        handleSudoComplete(result.sudoToken);
      }
    } catch (err) {
      setError(err.message || 'Invalid password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    handleSudoCancel();
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-md mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold">Verify Your Identity</h2>
                  <p className="text-white/80 text-sm">Sensitive action requires password</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Warning banner */}
          <div className="px-4 pt-4">
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-medium">Security Verification Required</p>
                <p className="text-amber-700 dark:text-amber-400 mt-1">
                  You're about to perform a sensitive action that requires additional verification.
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleVerify} className="p-4 space-y-4">
            {/* Password input */}
            <div className="space-y-2">
              <Label htmlFor="sudo-password">Password</Label>
              <PasswordInput
                id="sudo-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoFocus
                disabled={loading}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleClose}
                disabled={loading}
                data-testid="sudo-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                disabled={loading}
                data-testid="sudo-verify-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Verify
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Footer */}
          <div className="px-4 pb-4">
            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
              Sudo mode will remain active for 5 minutes after verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SudoModeModal;
